/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/


import {
	computed,
	type ElementRef,
	Injector,
	signal,
	Signal,
	type Type,
	type ViewContainerRef
} from "@angular/core";
import type { ThemeService } from "@app/@services/generic/theme.service";
import { Subject, takeUntil, tap } from "rxjs";
import { CbDialogConfig, CbDialogRef } from "./dialog.class";
import { DialogService } from "./dialog.service";

export abstract class DialogGeneric {
	public abstract dialogRef: Signal<CbDialogRef>;
	public abstract dialogConfig: Signal<CbDialogConfig>;

	protected abstract readonly injector: Injector;
	protected abstract readonly vcr: ViewContainerRef;
	protected abstract readonly themeService: ThemeService;
	protected abstract readonly dialogService: DialogService;

	private handlePopStateRef = this.handlePopState.bind(this);

	public closing = signal<boolean>(false);

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private _component!: Type<any>;

	/**
	 * The component to load inside the dialog.
	 */
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public set component(component: Type<any>) {
		this._component = component;

		// Create the injector for the component
		const injector = Injector.create({
			providers: [
				{
					provide: CbDialogRef,
					useValue: this.dialogRef(),
				},
				{
					provide: CbDialogConfig,
					useValue: this.dialogConfig(),
				},
			],
			parent: this.injector,
		});

		// Init the component inside the dialog
		const componentRef = this.vcr.createComponent(component, {
			injector: injector,
		});

		// Add the component to this.dialogWrapper
		this.dialogWrapper().nativeElement.appendChild(
			componentRef.location.nativeElement,
		);
	}

	protected onDestroy$ = new Subject<void>();

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public abstract dialogWrapper: Signal<ElementRef<any>>;

	protected destroy$: Subject<void> = new Subject<void>();

	constructor() {
		window.addEventListener("popstate", this.handlePopStateRef);
	}

	private handlePopState(event: PopStateEvent) {
		event.preventDefault();
		this.dialogService.backNavigationTriggered.next();
		history.forward();
	}

	protected init() {
		this.dialogRef().onClose
			.pipe(
				takeUntil(this.destroy$),
				tap(() => {
					this.closing.set(true);

					setTimeout(() => {
						this.onDestroy$.next();
					}, 120);
				}),
			)
			.subscribe();
	}

	public get theme(): string {
		return this.themeService.theme;
	}

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public get component(): Type<any> {
		return this._component;
	}

	public alignment = computed(() => {
		return this.dialogConfig().position || "center";
	});

	public width = computed(() => {
		return this.dialogConfig().width || "auto";
	});

	public height = computed(() => {
		return this.dialogConfig().height || "auto";
	});

	public backdropClick(): void {
		if (this.dialogConfig().closeOnBackdrop !== false) {
			this.dialogRef().close();
		}
	}

	protected destroy() {
		this.destroy$.next();
		this.destroy$.complete();
		window.removeEventListener("popstate", this.handlePopStateRef);
	}
}
